import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { BodyMeasurement, WeightUnitScope } from "@jilian/shared";
import type { GenerateWorkoutResult, ReadinessResult } from "@jilian/core";
import {
  generateWorkoutPlan,
  evaluateReadiness,
  nextTemplate,
  createDefaultTemplates,
} from "@jilian/core";
import { useAppStore } from "../state/useAppStore";
import { useSessionStore } from "../state/useSessionStore";
import {
  useMeasurements,
  useMeals,
  useSettings,
  useTodayIntent,
  useTrainingTemplates,
  useRecentSessions,
} from "../db/hooks";
import { saveMeasurement, setTodayIntent, loadNotificationSetting } from "../db/repo";
import { loadLatestWeights } from "../db/workoutRepo";
import {
  loadUserFatigueStatus,
  startDeloadWeek,
  cancelDeloadWeek,
  deferDeloadWeek,
  type UserFatigueStatus,
} from "../lib/fatigueData";
import { EXERCISE_LIBRARY } from "../data/exercises";
import { currentTimezone, localDateString } from "../lib/timezone";
import { todayTitle, formatKg } from "../lib/format";
import {
  planTypeTitle,
  weightScopeLabel,
  FATIGUE_INDEX_TITLE,
  ATL_TITLE,
  ATL_EXPLANATION,
  CTL_TITLE,
  CTL_EXPLANATION,
  TSB_TITLE,
  TSB_EXPLANATION,
} from "../lib/copy";
import { uuid } from "../lib/ids";
import { ReadinessForm } from "../components/ReadinessForm";
import { Button, Card, RiskTag, Sheet, Stat } from "../components/ui";

const DEVICE_ID = "device-demo-1";

export function Today() {
  const nav = useNavigate();
  const startSession = useSessionStore((s) => s.start);
  const setLastReadiness = useAppStore((s) => s.setLastReadiness);

  const [showReadiness, setShowReadiness] = useState(false);
  const [showDeloadConfirm, setShowDeloadConfirm] = useState(false);
  const [weightInput, setWeightInput] = useState("");
  const [lastWeights, setLastWeights] = useState<
    Map<string, { weightKg: number; weightUnitScope: WeightUnitScope }>
  >(new Map());
  const [fatigueStatus, setFatigueStatus] = useState<UserFatigueStatus | null>(null);

  const settings = useSettings();
  const todayDate = localDateString();

  const profile = settings?.profile ?? null;
  const injury = settings?.injury ?? null;
  const nutrition = settings?.nutrition ?? null;

  const today = useTodayIntent(profile?.id ?? "user-default", todayDate);
  const intent = today?.intent ?? null;
  const measurements = useMeasurements(profile?.id ?? "user-default") ?? [];
  const meals = useMeals(profile?.id ?? "user-default") ?? [];
  const dbTemplates = useTrainingTemplates(profile?.id ?? "user-default");
  const recentSessions = useRecentSessions(profile?.id ?? "user-default");

  // 加载疲劳评估状态与恢复周
  const refreshFatigueStatus = async () => {
    if (!profile) return;
    const status = await loadUserFatigueStatus(profile.id, todayDate);
    setFatigueStatus(status);
  };

  useEffect(() => {
    if (profile) {
      void refreshFatigueStatus();
      void loadLatestWeights(profile.id).then((m) => {
        setLastWeights(m);
      });
    }
  }, [profile?.id, todayDate, recentSessions?.length]);

  const currentTemplate = useMemo(() => {
    if (!profile) return null;
    const templates =
      dbTemplates && dbTemplates.length > 0 ? dbTemplates : createDefaultTemplates(profile.id);
    return nextTemplate(templates, recentSessions ?? []);
  }, [dbTemplates, profile, recentSessions]);

  const isDeloadActive = fatigueStatus?.isDeloadActive ?? false;

  // 计划生成：若恢复周激活，应用 deload 规则
  const previewPlan: GenerateWorkoutResult | null = useMemo(() => {
    if (!currentTemplate || !injury) return null;

    let readiness: ReadinessResult;
    if (isDeloadActive) {
      readiness = {
        planType: "deload",
        allowProgression: false,
        volumeMultiplier: 0.6,
        loadMultiplier: 1.0,
        requireExerciseRefilter: false,
        allowFailure: false,
        reasonCodes: ["DELOAD_WEEK_ACTIVE"],
      };
    } else {
      readiness = evaluateReadiness({
        sleepQuality: "good",
        fatigueLevel: "low",
        muscleSoreness: "none",
        footStatus: "stable",
        jointPain: "none",
      });
    }

    return generateWorkoutPlan({
      sequenceCode: currentTemplate.code,
      slots: currentTemplate.slots,
      isOptionalLightDay: currentTemplate.isOptionalLightDay,
      readiness,
      injury,
      exerciseLibrary: EXERCISE_LIBRARY,
      lastWeights,
    });
  }, [currentTemplate, injury, lastWeights, isDeloadActive]);

  const todayKcal = useMemo(
    () => meals.reduce((sum, m) => sum + (m.finalCalories ?? 0), 0),
    [meals],
  );
  const todayProtein = useMemo(
    () => meals.reduce((sum, m) => sum + (m.finalProtein ?? 0), 0),
    [meals],
  );

  const todayWeightRecord = useMemo(
    () => measurements.find((m) => m.date === todayDate),
    [measurements, todayDate],
  );
  const latestWeight = todayWeightRecord
    ? todayWeightRecord.weightKg
    : measurements.length > 0
      ? measurements[measurements.length - 1]!.weightKg
      : null;

  const completedTaskCount =
    (todayWeightRecord ? 1 : 0) + (intent === "train" ? 1 : 0) + (meals.length > 0 ? 1 : 0);
  const taskProgressPct = Math.round((completedTaskCount / 3) * 100);

  const [remindBanner, setRemindBanner] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const dismissKey = `jilian-remind-dismiss-${todayDate}`;
    const check = async () => {
      if (sessionStorage.getItem(dismissKey)) return;
      const s = await loadNotificationSetting();
      if (!s.enabled || !s.trainingReminderNotifications) return;
      const now = new Date();
      const hm = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
      if (hm >= s.fixedTime && !cancelled) {
        setRemindBanner(true);
      }
    };
    void check();
    const t = window.setInterval(check, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [todayDate]);

  // 骨架屏：settings 加载期间避免白屏或假数据
  if (!settings || !profile || !currentTemplate || !previewPlan) {
    return (
      <div className="space-y-4 pb-6 pt-4 animate-pulse">
        <div className="h-8 bg-black/5 dark:bg-white/10 rounded-lg w-48" />
        <div className="h-24 bg-black/5 dark:bg-white/10 rounded-2xl" />
        <div className="h-64 bg-black/5 dark:bg-white/10 rounded-2xl" />
        <div className="h-32 bg-black/5 dark:bg-white/10 rounded-2xl" />
      </div>
    );
  }

  const onDirectStart = async () => {
    await setTodayIntent(profile.id, todayDate, currentTimezone(), "train", DEVICE_ID);
    const readiness: ReadinessResult = isDeloadActive
      ? {
          planType: "deload",
          allowProgression: false,
          volumeMultiplier: 0.6,
          loadMultiplier: 1.0,
          requireExerciseRefilter: false,
          allowFailure: false,
          reasonCodes: ["DELOAD_WEEK_ACTIVE"],
        }
      : evaluateReadiness({
          sleepQuality: "good",
          fatigueLevel: "low",
          muscleSoreness: "none",
          footStatus: "stable",
          jointPain: "none",
        });
    setLastReadiness(readiness);
    await startSession(currentTemplate.code, previewPlan.exercises, readiness, profile.id);
    nav("/session");
  };

  const onReadinessComplete = async (result: ReadinessResult) => {
    await setTodayIntent(profile.id, todayDate, currentTimezone(), "train", DEVICE_ID);
    setLastReadiness(result);
    setShowReadiness(false);
    const plan = generateWorkoutPlan({
      sequenceCode: currentTemplate.code,
      slots: currentTemplate.slots,
      isOptionalLightDay: currentTemplate.isOptionalLightDay,
      readiness: result,
      injury: settings.injury,
      exerciseLibrary: EXERCISE_LIBRARY,
      lastWeights,
    });
    await startSession(currentTemplate.code, plan.exercises, result, profile.id);
    nav("/session");
  };

  const saveWeight = async () => {
    const w = parseFloat(weightInput);
    if (!Number.isFinite(w) || w <= 0) return;
    const m: BodyMeasurement = {
      id: uuid(),
      userId: profile.id,
      date: todayDate,
      weightKg: w,
      waistCm: null,
      bodyFatPercent: null,
      measurementContext: "morning_fasted",
      deviceId: DEVICE_ID,
      syncStatus: "local_only",
      createdAt: new Date().toISOString(),
    };
    await saveMeasurement(m);
    setWeightInput("");
  };

  const handleStartDeload = async () => {
    await startDeloadWeek(todayDate);
    setShowDeloadConfirm(false);
    await refreshFatigueStatus();
  };

  const handleCancelDeload = async () => {
    await cancelDeloadWeek();
    await refreshFatigueStatus();
  };

  const handleDeferDeload = async () => {
    await deferDeloadWeek(todayDate);
    setShowDeloadConfirm(false);
    await refreshFatigueStatus();
  };

  const fatigueResult = fatigueStatus?.result;
  const fatigueIndex = fatigueResult?.fatigueIndex ?? 0;
  const deloadUrgency = fatigueResult?.deloadUrgency ?? "none";

  return (
    <div className="space-y-4 pb-6">
      {/* 提醒横幅 */}
      {remindBanner && intent === null ? (
        <Card className="p-4 border-tone-warn/40 bg-tone-warn/10 backdrop-blur-xl">
          <div className="flex items-center justify-between mb-1.5">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-tone-warn">
              <span className="w-2 h-2 rounded-full bg-tone-warn animate-pulse" />
              训练提醒
            </span>
            <button
              onClick={() => {
                sessionStorage.setItem(`jilian-remind-dismiss-${todayDate}`, "1");
                setRemindBanner(false);
              }}
              className="text-xs text-tone-soft hover:text-tone-ink font-medium"
            >
              稍后提醒
            </button>
          </div>
          <p className="text-sm font-semibold text-tone-ink font-body">
            已到设定训练时间，今天准备开始训练吗？
          </p>
        </Card>
      ) : null}

      {/* 恢复周进行中提示横幅 */}
      {isDeloadActive && (
        <Card className="p-4 border-tone-accent/40 bg-tone-accentSoft/80 flex items-center justify-between">
          <div>
            <div className="text-[14px] font-bold text-tone-ink flex items-center gap-1.5">
              <span>🌟 主动恢复周进行中</span>
              <RiskTag tone="accent">剩余 {fatigueStatus?.deloadDaysRemaining ?? 7} 天</RiskTag>
            </div>
            <div className="text-[12px] text-tone-soft mt-0.5 font-caption">
              训练总组数已下调约 40%，余力 ≥3 次，禁止力竭，促进深层中枢恢复
            </div>
          </div>
          <button
            type="button"
            onClick={() => void handleCancelDeload()}
            className="text-xs font-bold text-tone-soft hover:text-tone-ink border border-tone-border px-2.5 py-1.5 rounded-lg bg-tone-card active:scale-95"
          >
            提前结束
          </button>
        </Card>
      )}

      {/* 头部状态与进度 */}
      <header className="pt-2">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[26px] font-bold text-tone-ink tracking-tight font-title">
              {todayTitle()}
            </h1>
            <div className="flex items-center gap-2 text-[12px] font-medium text-tone-soft mt-0.5 font-caption">
              <span className="flex items-center gap-1 text-tone-good font-semibold">
                <span className="w-2 h-2 rounded-full bg-tone-good" />
                本地保存
              </span>
              <span>•</span>
              <span>序列 {currentTemplate.code} · {currentTemplate.name}</span>
            </div>
          </div>
          <span className="text-[12px] font-bold text-tone-soft tabular-nums font-caption">
            今日完成 {completedTaskCount}/3
          </span>
        </div>
        <div className="h-1 w-full rounded-full bg-black/5 dark:bg-white/10 mt-2.5 overflow-hidden">
          <div
            className="h-full bg-tone-accent rounded-full transition-all duration-300"
            style={{ width: `${taskProgressPct}%` }}
          />
        </div>
      </header>

      {/* 1. 晨起空腹体重卡 */}
      <Card className="p-4 border border-tone-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span
              className={`w-5 h-5 rounded-full text-[11px] font-bold flex items-center justify-center ${
                todayWeightRecord
                  ? "bg-tone-good text-white"
                  : "bg-black/5 dark:bg-white/10 text-tone-soft"
              }`}
            >
              {todayWeightRecord ? "✓" : "1"}
            </span>
            <h3 className="text-[15px] font-bold text-tone-ink font-headline">晨起空腹体重</h3>
          </div>
          {latestWeight && (
            <span className="text-[15px] font-bold text-tone-ink tabular-nums font-display">
              {formatKg(latestWeight)}{" "}
              <span className="text-[12px] font-medium text-tone-soft font-caption">kg</span>
            </span>
          )}
        </div>

        {!todayWeightRecord ? (
          <div className="mt-2.5">
            <p className="text-xs text-tone-soft mb-2">今日尚未记录晨起体重：</p>
            <div className="flex gap-2">
              <input
                type="number"
                inputMode="decimal"
                value={weightInput}
                onChange={(e) => setWeightInput(e.target.value)}
                placeholder="输入今日体重（如 72.5）"
                className="flex-1 min-h-[44px] px-3.5 rounded-elem bg-black/5 dark:bg-white/10 text-sm font-semibold text-tone-ink placeholder:text-tone-soft/60"
              />
              <Button
                variant="secondary"
                className="min-h-[44px] px-4 font-bold text-xs rounded-elem"
                onClick={() => void saveWeight()}
                disabled={!weightInput}
              >
                保存记录
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-[12px] text-tone-good mt-1.5 pl-7 font-caption font-semibold flex items-center gap-1">
            <span>✓ 今日已记录（{formatKg(todayWeightRecord.weightKg)} kg）</span>
          </div>
        )}
      </Card>

      {/* 2. 今日训练计划卡 */}
      <Card className="p-5 border border-tone-border shadow-sm">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[11px] font-bold text-tone-accent tracking-wider uppercase font-caption">
              今日训练目标
            </div>
            <h2 className="text-[20px] font-bold text-tone-ink tracking-tight mt-0.5 font-title">
              序列 {currentTemplate.code}（{currentTemplate.name}）
            </h2>
            <div className="text-[12px] font-medium text-tone-soft mt-0.5 font-caption">
              预计 {previewPlan.estimatedMinutes} 分钟 · 共 {previewPlan.exercises.length} 个动作
            </div>
          </div>
          <RiskTag tone={isDeloadActive ? "accent" : "good"}>
            {planTypeTitle(previewPlan.planType)}
          </RiskTag>
        </div>

        <div className="my-3.5 p-3 rounded-elem bg-black/[0.02] dark:bg-white/[0.03] space-y-2 border border-tone-border/40">
          {previewPlan.exercises.map((p, idx) => (
            <div key={p.exercise.id} className="flex items-center justify-between text-[13px]">
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-black/5 dark:bg-white/10 text-[11px] font-bold text-tone-soft flex items-center justify-center font-display">
                  {idx + 1}
                </span>
                <span className="font-semibold text-tone-ink">{p.exercise.nameZh}</span>
              </div>
              <span className="text-[12px] font-bold text-tone-soft tabular-nums font-caption">
                {p.suggestedWeightKg != null ? (
                  <span className="text-tone-ink font-bold font-display">
                    {p.suggestedWeightKg} kg
                  </span>
                ) : (
                  weightScopeLabel(p.weightUnitScope)
                )}{" "}
                · {p.targetSets}组 × {p.repMin}–{p.repMax}次
              </span>
            </div>
          ))}
        </div>

        <div className="space-y-2 pt-1">
          <Button
            className="w-full min-h-[54px] text-[16px] font-bold flex items-center justify-center gap-2 shadow-sm"
            onClick={() => void onDirectStart()}
          >
            <span>开始今日训练</span>
            <span className="text-lg">→</span>
          </Button>

          <button
            type="button"
            onClick={() => setShowReadiness(true)}
            className="w-full min-h-[44px] text-[12px] font-semibold text-tone-soft hover:text-tone-ink transition-colors flex items-center justify-center gap-1 active:scale-95"
          >
            <span>今日状态问询（身体自评）</span>
            <span>›</span>
          </button>
        </div>
      </Card>

      {/* 3. 疲劳与恢复评估卡（确定性规则引擎接入） */}
      <Card className="p-4 border border-tone-border space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="text-[15px] font-bold text-tone-ink font-headline">
              🧠 疲劳积累与恢复评估
            </span>
          </div>
          {fatigueStatus?.hasEnoughData ? (
            <RiskTag
              tone={
                deloadUrgency === "mandatory"
                  ? "danger"
                  : deloadUrgency === "suggested"
                    ? "warn"
                    : "good"
              }
            >
              {deloadUrgency === "mandatory"
                ? "建议减量"
                : deloadUrgency === "suggested"
                  ? "关注疲劳"
                  : "状态充沛"}
            </RiskTag>
          ) : (
            <RiskTag tone="accent">数据积累中</RiskTag>
          )}
        </div>

        {!fatigueStatus?.hasEnoughData ? (
          <div className="p-3 rounded-elem bg-black/[0.02] dark:bg-white/[0.03] text-xs text-tone-soft leading-relaxed border border-tone-border/40">
            训练数据积累中（完成 2 次以上训练后自动计算中枢疲劳与恢复平衡指标）。
          </div>
        ) : (
          <div className="space-y-3">
            {/* 中枢疲劳指数进度条 */}
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="font-semibold text-tone-soft">{FATIGUE_INDEX_TITLE}</span>
                <span className="font-bold tabular-nums text-tone-ink font-display">
                  {fatigueIndex} / 100
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-black/5 dark:bg-white/10 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    fatigueIndex >= 75
                      ? "bg-tone-danger"
                      : fatigueIndex >= 50
                        ? "bg-tone-warn"
                        : "bg-tone-good"
                  }`}
                  style={{ width: `${Math.min(100, Math.max(5, fatigueIndex))}%` }}
                />
              </div>
            </div>

            {/* ATL / CTL / TSB 3 格紧凑指标 */}
            <div className="grid grid-cols-3 gap-2">
              <Stat
                label={ATL_TITLE}
                value={`${Math.round(fatigueResult?.acuteTrainingLoad ?? 0)}`}
                unit=""
                sub={ATL_EXPLANATION}
              />
              <Stat
                label={CTL_TITLE}
                value={`${Math.round(fatigueResult?.chronicTrainingLoad ?? 0)}`}
                unit=""
                sub={CTL_EXPLANATION}
              />
              <Stat
                label={TSB_TITLE}
                value={`${Math.round(fatigueResult?.trainingStressBalance ?? 0)}`}
                unit=""
                sub={TSB_EXPLANATION}
              />
            </div>

            {/* 恢复建议文案 */}
            {fatigueResult?.recommendationZh && (
              <div className="p-3 rounded-elem bg-black/[0.02] dark:bg-white/[0.03] text-xs text-tone-ink leading-relaxed border border-tone-border/40">
                💡 <strong>恢复建议</strong>：{fatigueResult.recommendationZh}
              </div>
            )}

            {/* 减量周提示或警示 */}
            {deloadUrgency === "mandatory" && !isDeloadActive && (
              <div className="p-3 rounded-elem bg-tone-danger/10 border border-tone-danger/30 space-y-2">
                <div className="text-xs font-bold text-tone-danger">
                  ⚠️ 警示：深层疲劳与中枢负荷已达临界点，建议本周主动减量。
                </div>
                <div className="flex gap-2">
                  <Button
                    className="flex-1 min-h-[38px] text-xs font-bold bg-tone-danger text-white"
                    onClick={() => setShowDeloadConfirm(true)}
                  >
                    开启恢复周
                  </Button>
                  <Button
                    variant="secondary"
                    className="min-h-[38px] px-3 text-xs"
                    onClick={() => void handleDeferDeload()}
                  >
                    延期一周
                  </Button>
                </div>
              </div>
            )}

            {deloadUrgency === "suggested" && !isDeloadActive && (
              <div className="p-3 rounded-elem bg-tone-warn/10 border border-tone-warn/30 space-y-2">
                <div className="text-xs font-bold text-tone-warn">
                  💡 提示：近期疲劳偏高，可考虑在接下来 1–2 周安排恢复周。
                </div>
                <Button
                  variant="secondary"
                  className="w-full min-h-[38px] text-xs font-bold"
                  onClick={() => setShowDeloadConfirm(true)}
                >
                  开启恢复周
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* 4. 饮食与营养摄入卡 */}
      <Card className="p-4 border border-tone-border space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span
              className={`w-5 h-5 rounded-full text-[11px] font-bold flex items-center justify-center ${
                meals.length > 0
                  ? "bg-tone-good text-white"
                  : "bg-black/5 dark:bg-white/10 text-tone-soft"
              }`}
            >
              {meals.length > 0 ? "✓" : "3"}
            </span>
            <h3 className="text-[15px] font-bold text-tone-ink font-headline">饮食与营养摄入</h3>
          </div>
          <button
            onClick={() => nav("/diet")}
            className="text-[12px] font-bold text-tone-accent hover:underline flex items-center gap-0.5"
          >
            <span>饮食记录</span>
            <span>›</span>
          </button>
        </div>

        <div className="flex items-center justify-between p-3 rounded-elem bg-black/[0.02] dark:bg-white/[0.03] border border-tone-border/40">
          <div>
            <div className="text-[11px] text-tone-soft font-medium font-caption">今日累计摄入</div>
            <div className="text-[20px] font-bold text-tone-ink tabular-nums mt-0.5 font-display">
              {todayKcal}{" "}
              <span className="text-[12px] font-medium text-tone-soft font-caption">
                / {nutrition?.calories ?? 2200} kcal
              </span>
              <span className="text-[12px] font-bold text-tone-good ml-2 font-caption">
                （蛋白 {todayProtein}g / {nutrition?.proteinGrams ?? 140}g）
              </span>
            </div>
          </div>
          <Button
            className="min-h-[40px] px-3.5 text-xs font-bold rounded-elem shadow-sm"
            onClick={() => nav("/diet")}
          >
            + 记一餐
          </Button>
        </div>
      </Card>

      {/* 恢复问询抽屉 */}
      <Sheet open={showReadiness} onClose={() => setShowReadiness(false)} title="训练前状态评估">
        <ReadinessForm onComplete={onReadinessComplete} />
      </Sheet>

      {/* 开启恢复周确认抽屉 */}
      <Sheet
        open={showDeloadConfirm}
        onClose={() => setShowDeloadConfirm(false)}
        title="开启主动恢复周"
      >
        <div className="p-4 space-y-3.5">
          <p className="text-xs text-tone-soft leading-relaxed">
            开启恢复周后，未来 7 天内生成的训练计划将自动应用减量规则：
          </p>
          <ul className="list-disc pl-4 text-xs text-tone-ink space-y-1">
            <li>总组数下调约 40%，大幅消除中枢神经疲劳；</li>
            <li>余力目标设为 ≥3 次（轻松），禁止训练至力竭；</li>
            <li>恢复周会话不计入连续失败，7 天后自动恢复正式训练。</li>
          </ul>
          <div className="pt-2 space-y-2">
            <Button
              className="w-full min-h-[46px] font-bold text-sm"
              onClick={() => void handleStartDeload()}
            >
              确认开启恢复周（7天）
            </Button>
            <Button
              variant="ghost"
              className="w-full min-h-[40px] text-xs"
              onClick={() => void handleDeferDeload()}
            >
              延期一周再评估
            </Button>
          </div>
        </div>
      </Sheet>
    </div>
  );
}